import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { UsageResponse, UsageRow, UsageSummary, UsageWindow } from '@one-deployment/api'
import { Card, PageHeader, Segmented, Select, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { EmptyState, ErrorState } from '@/components/status'
import { enc, get } from '@/lib/api'
import { dateTime, pct, tokens } from '@/lib/format'
import { qk } from '@/lib/query'
import { useOverview } from '@/features/shell/useOverview'

type Range = '24h' | '7d' | '30d'
type Group = UsageResponse['group']
const DAYS: Record<Range, number> = { '24h': 1, '7d': 7, '30d': 30 }

const day = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const total = (r: UsageRow) => r.inputTokens + r.outputTokens + r.cacheRead + r.cacheWrite

function useUsage(group: Group, from: string, to: string, project?: string) {
  return useQuery({
    queryKey: qk.usage(group, from, to, project),
    queryFn: ({ signal }) => get<UsageResponse>(`/api/usage?group=${group}&from=${from}&to=${to}${project ? `&project=${enc(project)}` : ''}`, signal),
    staleTime: 5 * 60_000,
  })
}

function useSummary(days: number, project?: string) {
  return useQuery({
    queryKey: qk.usageSummary(days, project),
    queryFn: ({ signal }) => get<UsageSummary>(`/api/usage/summary?days=${days}${project ? `&project=${enc(project)}` : ''}`, signal),
    staleTime: 60_000,
  })
}

/**
 * One window's estimate. The ceiling is learnt from past limit hits (Anthropic
 * publishes none), so until there has been a hit there is no percentage --
 * only what has been used, rather than a number made up to fill the space.
 */
function WindowMeter({ label, w }: { label: string; w: UsageWindow }) {
  const shown = w.percent === null ? null : Math.min(100, w.percent)
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span>{label}</span>
        <span className="tabular-nums">{w.percent === null ? '—' : `~${w.percent}%`}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-surface-3">
        {shown !== null && <div className={shown >= 90 ? 'h-full bg-warn' : 'h-full bg-accent'} style={{ width: `${shown}%` }} />}
      </div>
      <p className="text-2xs text-fg-faint">
        {tokens(w.used)} used{w.ceiling === null ? ' · no limit hit seen yet, so no ceiling to compare with' : ` of ~${tokens(w.ceiling)} (median of ${w.samples} past hit${w.samples === 1 ? '' : 's'})`}
      </p>
    </div>
  )
}

/** Claude usage (decision 19: measure only — no budgets, nothing here blocks work). */
export function UsagePage() {
  const search = useSearch({ strict: false }) as { range?: Range; project?: string }
  const range: Range = search.range ?? '7d'
  const project = search.project
  const nav = useNavigate()
  const overview = useOverview().data
  const projects = overview?.projects ?? []
  // Days, not instants: usage is rolled up per day, and a whole-day key keeps the cache stable within a day.
  const to = day(Date.now())
  const from = day(Date.now() - (DAYS[range] - 1) * 86_400_000)
  const byDay = useUsage('day', from, to, project)
  const byProject = useUsage('project', from, to, project)
  const byModel = useUsage('model', from, to, project)
  const summary = useSummary(DAYS[range], project)
  const setSearch = (s: { range?: Range; project?: string }) => void nav({ to: '/settings/usage', search: { range, project, ...s } })

  const error = byDay.error ?? byProject.error ?? byModel.error
  const ready = byDay.data && byProject.data && byModel.data
  const chart = (byDay.data?.rows ?? []).map((r) => ({ day: r.key, input: r.inputTokens, output: r.outputTokens, cache: r.cacheRead + r.cacheWrite }))
  const projectTotal = (byProject.data?.rows ?? []).reduce((n, r) => n + total(r), 0)
  const modelTotal = (byModel.data?.rows ?? []).reduce((n, r) => n + total(r), 0)
  const limit = overview?.claudeLimit

  return (
    <div>
      <PageHeader title="Usage" meta="Tokens per Claude session, from the transcripts. Measured only — nothing here pauses work.">
        <div className="flex items-center gap-2">
          <Segmented value={range} onChange={(r) => setSearch({ range: r })} options={[{ value: '24h', label: 'Today' }, { value: '7d', label: '7d' }, { value: '30d', label: '30d' }]} />
          <Select value={project ?? ''} onChange={(e) => setSearch({ project: e.target.value || undefined })} aria-label="Project">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </Select>
        </div>
      </PageHeader>
      {error && !ready ? <ErrorState error={error} retry={() => { void byDay.refetch(); void byProject.refetch(); void byModel.refetch() }} /> : !ready ? <div className="px-6 py-5"><SkeletonRows rows={8} /></div> : (
        <div className="grid grid-cols-[1fr_260px] gap-4 px-6 py-5">
          <div className="space-y-4">
            <Card title="Tokens per day">
              {chart.length === 0 ? <EmptyState title="No sessions in this range." /> : (
                <div className="h-64 p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart}>
                      <CartesianGrid stroke="#23252a" vertical={false} />
                      <XAxis dataKey="day" stroke="#62666d" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="#62666d" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v: number) => tokens(v)} width={44} />
                      <Tooltip cursor={{ fill: '#1c1d20' }} contentStyle={{ background: '#151618', border: '1px solid #2e3036', borderRadius: 6, fontSize: 12 }} formatter={(v) => tokens(Number(v))} />
                      <Bar dataKey="input" stackId="a" fill="#5e6ad2" />
                      <Bar dataKey="output" stackId="a" fill="#4cb782" />
                      <Bar dataKey="cache" stackId="a" fill="#8a8f98" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
            <div className="grid grid-cols-2 gap-4">
              <Card title="By project">
                {byProject.data.rows.length === 0 ? <div className="px-3 py-6 text-center text-xs text-fg-subtle">No data.</div> : (
                  <Table head={['Project', 'Tokens', 'Sessions', 'Share']}>
                    {byProject.data.rows.map((r) => (
                      <Tr key={r.key}><Td>{r.key}</Td><Td className="tabular-nums">{tokens(total(r))}</Td><Td className="tabular-nums text-fg-subtle">{r.sessions}</Td><Td className="tabular-nums text-fg-subtle">{projectTotal ? pct((total(r) / projectTotal) * 100) : '—'}</Td></Tr>
                    ))}
                  </Table>
                )}
              </Card>
              <Card title="By model">
                {byModel.data.rows.length === 0 ? <div className="px-3 py-6 text-center text-xs text-fg-subtle">No data.</div> : (
                  <Table head={['Model', 'Tokens', 'Sessions', 'Share']}>
                    {byModel.data.rows.map((r) => (
                      <Tr key={r.key}><Td mono>{r.key}</Td><Td className="tabular-nums">{tokens(total(r))}</Td><Td className="tabular-nums text-fg-subtle">{r.sessions}</Td><Td className="tabular-nums text-fg-subtle">{modelTotal ? pct((total(r) / modelTotal) * 100) : '—'}</Td></Tr>
                    ))}
                  </Table>
                )}
              </Card>
            </div>
          </div>
          <div className="space-y-4">
          <Card title="Subscription windows">
            <div className="space-y-4 p-3 text-sm">
              {!summary.data ? <SkeletonRows rows={3} /> : (
                <>
                  {summary.data.accounts.map((a) => (
                    <div key={a.accountId ?? 'project'} className="space-y-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate font-medium">{a.label}</span>
                        <span className={a.state === 'ok' ? 'text-2xs text-ok' : 'text-2xs text-warn'}>
                          {a.state === 'ok' ? 'ok' : a.state === 'invalid' ? 'needs sign-in' : `limited${a.resetsAt ? ` until ${dateTime(a.resetsAt)}` : ''}`}
                        </span>
                      </div>
                      <WindowMeter label="5-hour window" w={a.windows.fiveHour} />
                      <WindowMeter label="Week" w={a.windows.week} />
                      {a.lastHitAt && <p className="text-2xs text-fg-faint">Last limit hit {dateTime(a.lastHitAt)}.</p>}
                    </div>
                  ))}
                  <p className="text-2xs text-fg-faint">
                    Estimates per Claude account. Counts input, output and cache writes across every project; the ceiling is what the window held when that account last ran out.
                  </p>
                </>
              )}
            </div>
          </Card>
          <Card title="Costliest tickets">
            {!summary.data ? <div className="p-3"><SkeletonRows rows={4} /></div> : summary.data.topTickets.length === 0 ? <div className="px-3 py-6 text-center text-xs text-fg-subtle">No ticket work in this range.</div> : (
              <Table head={['Ticket', 'Tokens']}>
                {summary.data.topTickets.map((t) => (
                  <Tr key={`${t.projectKey}/${t.ticket}`}>
                    <Td mono><Link to="/p/$project/w/$ticket" params={{ project: t.projectKey, ticket: t.ticket }} className="hover:text-accent-hover" title={t.title}>{t.ticket}</Link></Td>
                    <Td className="tabular-nums">{tokens(t.tokens)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Card>
          <Card title="Limit state">
            <div className="space-y-3 p-3 text-sm">
              {!limit ? <SkeletonRows rows={2} /> : limit.limited ? (
                <span className="text-warn">Every Claude account is at its limit — work paused{limit.resetsAt ? ` until ${dateTime(limit.resetsAt)}` : ''}, resumes automatically.</span>
              ) : (
                <span className="text-ok">An account is available — work is running.</span>
              )}
              <p className="text-2xs text-fg-faint">Claude runs on subscriptions only. At a limit a session moves to another account; work pauses only when none is left, never falling back to an API key.</p>
            </div>
          </Card>
          </div>
        </div>
      )}
    </div>
  )
}
